import { format, differenceInMinutes, isValid, parse, parseISO as dateFnsParseISO } from 'date-fns';

// Função para formatar data de forma segura com formato específico
export const formatSafeDateWithFormat = (date: Date | string | null | undefined, dateFormat: string = 'yyyy-MM-dd') => {
  if (!date) return '';

  try {
    // Se for string, converter para Date
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    
    // Verificar se é uma data válida
    if (!dateObj || !(dateObj instanceof Date) || isNaN(dateObj.getTime())) {
      return '';
    }
    
    return format(dateObj, dateFormat);
  } catch (error) {
    return '';
  }
};

// Funções auxiliares para voos
export const getTimeFromISOString = (isoString: string | undefined): string => {
  if (!isoString || !isoString.includes('T')) return '00:00';
  return isoString.split('T')[1].substring(0, 5); // Returns HH:mm
};

export const getDateFromISOString = (isoString: string | undefined): string => {
  if (!isoString || !isoString.includes('T')) return format(new Date(), 'yyyy-MM-dd');
  return isoString.split('T')[0]; // Returns YYYY-MM-DD
};

// Update the calculateMinutesBetweenISOStrings function to never return negative values
export const calculateMinutesBetweenISOStrings = (start: string, end: string): number => {
  if (!start || !end) return 0;
  
  try {
    const startDate = new Date(start);
    const endDate = new Date(end);
    
    if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
      return 0;
    }
    
    // Calculate the difference and ensure it's never negative
    const diffInMinutes = differenceInMinutes(endDate, startDate);
    return Math.max(0, diffInMinutes); // Return 0 if the difference is negative
  } catch (error) {
    return 0;
  }
};

// Update the formatDuration function to use HH:MM format
export const formatDuration = (minutes: number): string => {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  
  // Format as HH:MM
  return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
};

// Helper function to parse duration string like "2h 30m" or "02:30" into minutes
export const parseDurationToMinutes = (duration: string): number => {
  if (!duration) return 0;
  
  try {
    // Handle "HH:MM" format
    if (duration.includes(':')) {
      const [hours, minutes] = duration.split(':').map(part => parseInt(part, 10));
      return (hours || 0) * 60 + (minutes || 0);
    }
    
    // Handle "Xh Ym" format for backward compatibility
    const hoursMatch = duration.match(/(\d+)\s*h/);
    const minutesMatch = duration.match(/(\d+)\s*m/);
    
    const hours = hoursMatch ? parseInt(hoursMatch[1], 10) : 0;
    const minutes = minutesMatch ? parseInt(minutesMatch[1], 10) : 0;
    
    return hours * 60 + minutes;
  } catch (error) {
    return 0;
  }
};

export const parseISO = (dateString: string | undefined): Date | null => {
  if (!dateString) return null;
  
  try {
    // Assegurar que a string da data está no formato ISO
    if (!dateString.includes('T')) {
      dateString = `${dateString}T00:00:00`;
    }
    
    const parsedDate = parse(dateString, "yyyy-MM-dd'T'HH:mm:ss", new Date());
    return isValid(parsedDate) ? parsedDate : null;
  } catch (error) {
    return null;
  }
};

// Verifica se o número do voo segue o padrão correto (2 letras seguidas de 1-4 números)
export const validateFlightNumber = (number: string): boolean => {
  if (!number) return false;
  
  // Remover espaços e converter para maiúsculas
  const formattedNumber = formatFlightNumber(number);
  
  // Verificar o padrão: 2 letras seguidas de 1-4 números
  const regex = /^[A-Z]{2}\d{1,4}$/;
  return regex.test(formattedNumber);
};

// Formata o número do voo para o padrão esperado pela API (2 letras maiúsculas seguidas de números)
export const formatFlightNumber = (number: string): string => {
  if (!number) return '';
  
  // Remover espaços e converter para maiúsculas
  let formatted = number.replace(/\s+/g, '').toUpperCase();
  
  // Se o formato é algo como "LA 1234" ou "la1234", converter para "LA1234"
  const match = formatted.match(/^([A-Z]{2})(\d{1,4})$/);
  if (match) {
    return `${match[1]}${match[2]}`;
  }
  
  return formatted;
};

// Mapeamento básico de códigos de companhias aéreas para nomes
export const getAirlineName = (carrierCode: string) => {
  const airlines: Record<string, string> = {
    'LA': 'LATAM Airlines',
    'G3': 'GOL Linhas Aéreas',
    'AD': 'Azul Linhas Aéreas',
    'AA': 'American Airlines',
    'DL': 'Delta Air Lines',
    'UA': 'United Airlines',
    'BA': 'British Airways',
    'LH': 'Lufthansa',
    'AF': 'Air France',
    'KL': 'KLM',
    'IB': 'Iberia',
    'AZ': 'Alitalia',
    'EK': 'Emirates',
    'QR': 'Qatar Airways',
    'EY': 'Etihad Airways',
    'TK': 'Turkish Airlines',
    'SQ': 'Singapore Airlines',
    'CX': 'Cathay Pacific',
    'JL': 'Japan Airlines',
    'NH': 'ANA All Nippon Airways',
  };
  
  return airlines[carrierCode] || carrierCode;
}; 